/**
 * Pure decision logic for consent-gated Sentry Session Replay.
 *
 * Regulatory split (mirrors the PostHog treatment in `src/pages/_app.tsx`):
 * - Error / issue tracking runs WITHOUT consent. It is strictly necessary
 *   monitoring of service integrity and security (legitimate interest), and
 *   the Sentry error SDK sets no cookies or persistent identifiers.
 * - Session Replay records the user's session and is classified under the
 *   "Session Recording — Requires consent" category of the Braiv cookie
 *   policy. It must only run when the `recording` flag of the braiv_consent
 *   cookie is granted, and never when Global Privacy Control is active.
 *
 * Keeping this decision pure lets the repo's focused tests cover it without
 * a browser or the Sentry SDK.
 */

/**
 * Replay stays restricted to the production app host. Dev/staging domains are
 * excluded because Replay walks the DOM and triggers noisy browser console
 * errors with secure third-party iframes (Stripe, Firebase Auth).
 */
export const SENTRY_REPLAY_HOSTNAME = "app.braiv.co";

export interface ReplayConsentState {
    /** Current `window.location.hostname`. */
    hostname: string;
    /** Global Privacy Control hard-kill — same override PostHog/GTM honour. */
    gpcActive: boolean;
    /** `recording` flag from the braiv_consent cookie (missing cookie => false). */
    recordingConsent: boolean;
    /**
     * Current `window.location.pathname`. Embed player routes never record:
     * LMS iframes and crawlers have no banner, and Sentry Replay's hydration
     * detector mis-reports Next.js chunk-load failures as Hydration Error
     * (JAVASCRIPT-NEXTJS-69).
     */
    pathname?: string | null;
}

/**
 * Public embed player routes. Same surfaces that skip the consent banner,
 * Stripe preload, and GTM.
 */
export function isEmbedPlayerPath(pathname: string | null | undefined): boolean {
    if (!pathname) return false;
    const path = pathname.split("?")[0].split("#")[0] || "/";
    return path === "/embed" || path.startsWith("/embed/");
}

export function shouldEnableSentryReplay(state: ReplayConsentState): boolean {
    if (state.hostname !== SENTRY_REPLAY_HOSTNAME) return false;
    if (state.gpcActive) return false;
    if (!state.recordingConsent) return false;
    if (isEmbedPlayerPath(state.pathname)) return false;
    return true;
}
